import { create } from 'zustand';

export interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  avatar?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  login: (user: User, token: string, refreshToken: string) => void;
  logout: () => void;
}

// SECURITY (web XSS hardening): the refresh token is NO LONGER persisted to
// localStorage — it lives in an httpOnly `jarvis_refresh` cookie the backend
// sets on login/refresh (JavaScript, and therefore any XSS, cannot read it).
// We keep it in memory only (for the current tab) and rely on the cookie to
// re-mint an access token after a reload. The short-lived access token + user
// stay in localStorage so a reload doesn't immediately bounce to login.
// One-time migration: purge any pre-existing plaintext refresh token.
localStorage.removeItem('jarvis_refresh_token');

const storedToken = localStorage.getItem('jarvis_token');
const storedUser = localStorage.getItem('jarvis_user');

export const useAuthStore = create<AuthState>((set) => ({
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken,
  refreshToken: null, // memory-only; never read from / written to localStorage
  isAuthenticated: !!storedToken,

  setUser: (user) => {
    if (user) {
      localStorage.setItem('jarvis_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('jarvis_user');
    }
    set({ user });
  },

  setToken: (token) => {
    if (token) {
      localStorage.setItem('jarvis_token', token);
    } else {
      localStorage.removeItem('jarvis_token');
    }
    set({ token, isAuthenticated: !!token });
  },

  // Memory-only: the durable copy is the httpOnly cookie, not localStorage.
  setRefreshToken: (refreshToken) => {
    set({ refreshToken });
  },

  login: (user, token, refreshToken) => {
    localStorage.setItem('jarvis_user', JSON.stringify(user));
    localStorage.setItem('jarvis_token', token);
    // refreshToken intentionally NOT written to localStorage (httpOnly cookie).
    set({ user, token, refreshToken, isAuthenticated: true });
  },

  logout: () => {
    // Best-effort: clear the httpOnly refresh cookie server-side (capture the
    // access token BEFORE we wipe local storage).
    try {
      const base = (import.meta.env.VITE_API_URL as string) || '/api/v1';
      const accessToken = localStorage.getItem('jarvis_token');
      void fetch(`${base}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      }).catch(() => {});
    } catch {
      // ignore
    }
    localStorage.removeItem('jarvis_user');
    localStorage.removeItem('jarvis_token');
    localStorage.removeItem('jarvis_refresh_token'); // legacy cleanup
    set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
    // Force redirect to login
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
      window.location.replace('/');
    }
  },
}));
